import { useSearchParams } from "react-router-dom";
import {
  useOrdersSuccessReadQuery,
  useQuickPayStatusQuery,
} from "../store/api.ts";
import { clearCart } from "../store/cartSlice.js";
import { logPurchase } from "../utils/analytics";
import { useEffect } from "react";
import NewNavbar from "../components/NewNavbar.js";
import Footer from "../components/Footer.js";
import { useDispatch } from "react-redux";
// import { CheckCircle, Clock, AlertCircle } from "lucide-react"; // or any other icon library
import { getOrderBundlePricing,groupOrderItems } from "../utils/orderBundle";
import {
  formatPrice,
  getFirstAvailableAmount,
  getOrderItemQuantity,
  getOrderItemLineTotal,
  getOrderItemSavings,
  hasOrderItemDiscount,
  getOrderTotalAmount,
  getOrderSavingsAmount,
  toAmount,
} from "../utils.js";

const getQuantity = getOrderItemQuantity;

const getOriginalUnitPrice = (item) =>
  getFirstAvailableAmount(
    item?.original_price,
    item?.variant?.price,
    item?.unit_price,
  );

const getUnitPrice = (item) =>
  getFirstAvailableAmount(
    item?.unit_price,
    item?.variant?.price,
    item?.original_price,
  );

const getLineTotal = (item) => getOrderItemLineTotal(item, getUnitPrice);

const getItemSavings = (item) =>
  getOrderItemSavings({
    item,
    getOriginalUnitPrice,
    getLineTotal,
  });

const hasDiscount = (item) => hasOrderItemDiscount(item, getItemSavings);

const getOrderTotal = (order) => getOrderTotalAmount(order, getLineTotal);

const getOrderSavings = (order) => getOrderSavingsAmount(order, getItemSavings);

const isPaidOrder = (order) => {
  const status = String(order?.status || "").toLowerCase();

  return (
    Boolean(order?.paid_at) ||
    status.includes("paid") ||
    status.includes("completed") ||
    status.includes("confirmed")
  );
};

const buildPurchaseAnalyticsPayload = (order) => {
  const items = Array.isArray(order?.items)
    ? order.items.map((item, index) => {
        const variant = item?.variant || {};
        const quantity = Number(item?.quantity || 1);
        const unitPrice = Number(item?.unit_price || variant?.price || 0);

        return {
          item_id: String(variant?.id || item?.id || `order-item-${index}`),
          item_name: variant?.product || "Product",
          price: unitPrice,
          quantity,
          currency: "USD",
          item_variant: variant?.sku || String(variant?.id || ""),
          item_category: "Fashion",
          item_size: variant?.size || undefined,
          item_color: variant?.color || undefined,
          discount: Number(item?.savings || 0),
        };
      })
    : [];

  return {
    transactionId: order?.id,
    value: Number(order?.total_amount || 0),
    items,
  };
};

const PaymentSuccessItemCard = ({ item, insideBundle = false }) => {

  const quantity = getQuantity(item);
  const originalUnitPrice = getOriginalUnitPrice(item);
  const unitPrice = getUnitPrice(item);
  const originalLineTotal = originalUnitPrice * quantity;
  const lineTotal = getLineTotal(item);
  const savings = getItemSavings(item);
  const itemHasDiscount = hasDiscount(item);
  const imageUrl = item?.variant?.image_url;
  const hasImage = imageUrl && imageUrl !== "null";

  return (
    <div className={[
        "bg-white p-4",
        insideBundle
          ? ""
          : "rounded-[6px] border border-[#E1E1E1]",
      ].join(" ")}
    >
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="h-[112px] w-full shrink-0 overflow-hidden rounded-[5px] bg-[#EFEFEF] sm:w-[96px]">
          {hasImage ? (
            <img
              src={imageUrl}
              alt={String(
                item?.variant?.product ||
                  "Purchased product",
              )}
              className="h-full w-full object-cover object-center"
            />
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-between gap-4 sm:flex-row">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-inter text-[16px] font-medium leading-[140%] text-black">
                {item?.variant?.product ||
                  "Product"}
              </h3>
              {!insideBundle &&
              toAmount(item?.discount_percent) > 0 ? (
                <span className="rounded-[3px] bg-[#EAF7EE] px-1.5 py-0.5 font-spaceMono text-[10px] font-medium text-[#257A45]">
                  {toAmount(item.discount_percent)}
                  % OFF
                </span>
              ) : null}
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 font-inter text-[13px] leading-[150%] text-[#555555] sm:grid-cols-2">
              <p>
                <span className="text-[#888888]">
                  Size:
                </span>{" "}
                {item?.variant?.size?.toUpperCase() ||
                  "—"}
              </p>
              <p className="flex items-center gap-2">
                <span className="text-[#888888]">
                  Color:
                </span>
                {item?.variant?.color ? (
                  <span
                    className="inline-block h-3 w-3 rounded-full border border-[#D9D9D9]"
                    style={{
                      backgroundColor:
                        item.variant.color,
                    }}
                  />
                ) : (
                  "—"
                )}
              </p>
              {item?.variant?.sku ? (
                <p className="sm:col-span-2">
                  <span className="text-[#888888]">
                    SKU:
                  </span>{" "}
                  {item.variant.sku}
                </p>
              ) : null}
            </div>
          </div>

          <div className="shrink-0 text-left sm:text-right">
            {itemHasDiscount ? (
              <span className="font-spaceMono text-[11px] text-[#999999] line-through">
                $
                {formatPrice(
                  originalUnitPrice,
                )}
              </span>
            ) : null}

            <p className="mt-1 font-spaceMono text-[11px] text-[#555555]">
              ${formatPrice(unitPrice)} ×{" "}
              {quantity}
            </p>

            {itemHasDiscount ? (
              <p className="mt-2 font-inter text-[13px] text-[#999999] line-through">
                $
                {formatPrice(
                  originalLineTotal,
                )}
              </p>
            ) : null}

            <p className="mt-1 font-inter text-[17px] font-medium text-black">
              $
              {formatPrice(
                lineTotal,
              )}
            </p>

            {!insideBundle &&
            savings > 0 ? (
              <p className="mt-1 font-inter text-[12px] font-medium text-[#257A45]">
                You saved $
                {formatPrice(savings)}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentSuccessBundleCard = ({ bundle }) => {
  const pricing = getOrderBundlePricing(bundle);
  return (
    <li className="overflow-hidden rounded-[6px] border border-[#D8D3CC] bg-white">
      {/* BUNDLE HEADER */}
      <div className="border-b border-[#DDD8D1] bg-[#F7F4EF] px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="inline-flex rounded-[3px] bg-black px-2 py-1 font-spaceMono text-[8px] sm:text-[12px] font-bold uppercase tracking-[0.16em] text-white">
              Bundle
            </span>

            <h3 className="mt-2 font-inter text-[16px] font-medium text-[#111111]">
              {bundle?.name ||
                "Collection Bundle"}
            </h3>

            <p className="mt-1 font-spaceMono text-[9px] sm:text-[12px] uppercase tracking-[0.08em] text-[#777777]">
              {bundle?.items?.length || 0}{" "}
              products
              {pricing.quantity > 1
                ? ` • Qty ${pricing.quantity}`
                : ""}
            </p>
          </div>

          {pricing.discountPercent > 0 ? (
            <span className="rounded-[3px] bg-[#EAF7EE] px-2 py-1 font-spaceMono text-[10px] sm:text-[12px] font-semibold text-[#257A45]">
              {pricing.discountPercent} % BUNDLE SAVINGS
            </span>
          ) : null}
        </div>
      </div>

      {/* BUNDLE PRODUCTS */}
      <div className="divide-y divide-[#E7E3DD]">
        {(bundle?.items || []).map(
          (item, index) => (
            <PaymentSuccessItemCard
              key={item?.id || `${bundle.id}-${index}`}
              item={item}
              insideBundle
            />
          ),
        )}
      </div>

      {/* BUNDLE PRICE */}
      <div className="border-t border-[#DDD8D1] bg-[#FAF9F7] px-4 py-4">
        <div className="flex justify-between font-spaceMono text-[10px] uppercase text-[#6A7282]">
          <span>
            Original bundle price
          </span>
          <span>
            ${formatPrice(pricing.originalSubtotal)}
          </span>
        </div>

        {pricing.discountAmount >
        0 ? (
          <div className="mt-2 flex justify-between font-spaceMono text-[10px] font-semibold uppercase text-[#257A45]">
            <span>
              Bundle savings ({pricing.discountPercent}%)
            </span>
            <span>
              -${formatPrice(pricing.discountAmount)}
            </span>
          </div>
        ) : null}

        <div className="mt-3 flex justify-between border-t border-[#DDD8D1] pt-3 font-spaceMono text-[11px] font-bold uppercase text-[#313131]">
          <span>Bundle Total</span>
          <span>
            $
            {formatPrice(
              pricing.total,
            )}
          </span>
        </div>
      </div>
    </li>
  );
};

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  // Stripe Checkout comes back with a session_id. Quick pay has no session and
  // names the order directly.
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const dispatch = useDispatch();

  const sessionQuery = useOrdersSuccessReadQuery(
    { sessionId },
    { skip: !sessionId },
  );

  const quickPayQuery = useQuickPayStatusQuery(
    { orderId },
    { skip: Boolean(sessionId) || !orderId },
  );

  const {
    data: order,
    isSuccess,
    isLoading,
    isError,
  } = sessionId ? sessionQuery : quickPayQuery;

  useEffect(() => {
    if (!isSuccess || !order) return;
    dispatch(clearCart());

    if (!isPaidOrder(order)) {
      console.log("Purchase event not fired because order is not paid yet.", {
        orderId: order?.id,
        status: order?.status,
        paidAt: order?.paid_at,
      });
      return;
    }
    
    logPurchase(buildPurchaseAnalyticsPayload(order));
  }, [isSuccess, order, dispatch]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] text-[#111111]">
        <NewNavbar />
        <main className="mx-auto flex min-h-[70vh] w-full max-w-[1540px] items-center justify-center px-4 md:px-6">
          <div className="rounded-[6px] border border-[#DCDCDC] bg-white px-8 py-10 text-center shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
            <div className="mx-auto mb-5 h-9 w-9 animate-spin rounded-full border border-[#D9D9D9] border-t-[#111111]" />

            <p className="font-spaceMono text-[11px] uppercase tracking-[0.16em] text-[#777777]">
              Loading order
            </p>

            <h1 className="mt-3 font-inter text-[24px] font-semibold tracking-[-0.03em] text-black">
              Fetching your order details
            </h1>
          </div>
        </main>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] text-[#111111]">
        <NewNavbar />

        <main className="mx-auto flex min-h-[70vh] w-full max-w-[1540px] items-center justify-center px-4 md:px-6">
          <div className="w-full max-w-[520px] rounded-[6px] border border-[#DCDCDC] bg-white px-6 py-9 text-center shadow-[0_1px_2px_rgba(0,0,0,0.03)] sm:px-8">
            <p className="font-spaceMono text-[11px] uppercase tracking-[0.16em] text-[#777777]">
              Order status
            </p>

            <h1 className="mt-4 font-inter text-[34px] font-semibold leading-[100%] tracking-[-0.04em] text-black sm:text-[42px]">
              Order Not Found
            </h1>

            <p className="mx-auto mt-4 max-w-[360px] font-inter text-[14px] leading-[165%] text-[#555555]">
              Thank you for your order. We could not load the order details for
              this checkout session.
            </p>

            <a
              href="/"
              className="mt-7 inline-flex h-[40px] items-center justify-center rounded-[5px] bg-[#313131] px-6 font-inter text-[13px] font-medium text-white transition hover:bg-black"
            >
              Return to Home
            </a>
          </div>
        </main>
      </div>
    );
  }

  const totalAmount = getOrderTotal(order);
  const discountedAmount = getOrderSavings(order);
  const originalAmount = totalAmount + discountedAmount;
  const orderEntries = groupOrderItems(order?.items || []);

  const status = String(order.status || "").toLowerCase();

  const isPending =
    status.includes("pending") ||
    status.includes("under processing") ||
    status.includes("under preparation");

  return (
    <>
      <div className="min-h-screen bg-[#F7F7F7] text-[#111111]">
        <NewNavbar />

        <main className="mx-auto w-full max-w-[1540px] px-4 pt-10 pb-16 md:px-6">
          <section className="rounded-[6px] border border-[#DCDCDC] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
            <div className="border-b border-[#D9D9D9] px-5 py-8 sm:px-7 md:px-10 md:py-10">
              <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                <div>
                  <h1 className="mt-4 font-inter text-[38px] font-semibold leading-[100%] tracking-[-0.04em] text-black sm:text-[48px] md:text-[56px]">
                    Thank You For Your Order
                  </h1>

                  <p className="mt-5 max-w-[560px] font-inter text-[14px] leading-[165%] text-[#555555] sm:text-[15px]">
                    Your order has been received successfully. Review your order
                    summary below or continue shopping.
                  </p>
                </div>

                <div
                  className={`
                  inline-flex h-[34px] shrink-0 items-center justify-center rounded-[5px]
                  border px-4 font-spaceMono text-[10px] uppercase tracking-[0.12em]
                  ${
                    isPending
                      ? "border-[#F26633]/40 bg-[#FFF3EE] text-[#F26633]"
                      : "border-[#DCDCDC] bg-[#FAFAFA] text-[#111111]"
                  }
                `}
                >
                  {order.status || "Confirmed"}
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="grid grid-cols-1 gap-0 lg:grid-cols-[1fr_360px]">
              <div className="px-5 py-7 sm:px-7 md:px-10">
                <div className="mb-5 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-spaceMono text-[10px] uppercase tracking-[0.14em] text-[#777777]">
                      Order Summary
                    </p>

                    <h2 className="mt-2 font-inter text-[22px] font-medium tracking-[-0.03em] text-black">
                      Purchased Items
                    </h2>
                  </div>

                  <p className="font-spaceMono text-[11px] text-[#777777]">
                    {order.items.length} item
                    {order.items.length === 1 ? "" : "s"}
                  </p>
                </div>

                <ul className="space-y-4">
                  {orderEntries.map((entry) => {
                      if (entry.type === "bundle") {
                        return (
                          <PaymentSuccessBundleCard
                            key={entry.key}
                            bundle={entry}
                          />
                        );
                      }
                      return (
                        <li key={entry.key}>
                          <PaymentSuccessItemCard
                            item={entry.item}
                          />
                      </li>
                    );
                  })}
                </ul>
              </div>

              <aside className="border-t border-[#D9D9D9] bg-[#FAFAFA] px-5 py-7 sm:px-7 lg:border-l lg:border-t-0 md:px-8">
                <p className="font-spaceMono text-[10px] uppercase tracking-[0.14em] text-[#777777]">
                  Payment Summary
                </p>

                <div className="mt-5 space-y-4">
                  <div className="flex items-center justify-between gap-4 border-b border-[#D9D9D9] pb-4">
                    <p className="font-inter text-[14px] text-[#555555]">
                      Subtotal
                    </p>

                    <p className="font-inter text-[14px] font-medium text-black">
                      ${formatPrice(originalAmount)}
                    </p>
                  </div>

                  {discountedAmount > 0 ? (
                    <div className="flex items-center justify-between gap-4 border-b border-[#D9D9D9] pb-4">
                      <p className="font-inter text-[14px] text-[#257A45]">
                        Discount
                      </p>

                      <p className="font-inter text-[14px] font-medium text-[#257A45]">
                        -${formatPrice(discountedAmount)}
                      </p>
                    </div>
                  ) : null}

                  <div className="flex items-center justify-between gap-4 pt-1">
                    <p className="font-inter text-[16px] font-medium text-black">
                      Total
                    </p>

                    <p className="font-inter text-[22px] font-semibold tracking-[-0.03em] text-black">
                      ${formatPrice(totalAmount)}
                    </p>
                  </div>

                  {discountedAmount > 0 ? (
                    <div className="rounded-[5px] border border-[#CFE8D7] bg-[#F1FAF4] px-3 py-2.5 text-center font-inter text-[12px] font-medium text-[#257A45]">
                      You saved ${formatPrice(discountedAmount)} on this order
                    </div>
                  ) : null}
                </div>

                <div className="mt-8 space-y-3">
                  <a
                    href="/"
                    className="inline-flex h-[42px] w-full items-center justify-center rounded-[5px] bg-[#313131] px-5 font-inter text-[13px] font-medium text-white transition hover:bg-black"
                  >
                    Continue Shopping
                  </a>

                  <a
                    href="/account/orders"
                    className="inline-flex h-[42px] w-full items-center justify-center rounded-[5px] border border-[#D9D5D0] bg-white px-5 font-inter text-[13px] font-medium text-[#1E1E1E] transition hover:bg-[#F6F6F6]"
                  >
                    View Order History
                  </a>
                </div>
              </aside>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </>
  );
}
